import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Pane,
  Heading,
  Spinner,
  Text,
  Card,
  Strong,
  Alert,
  Paragraph,
  Table,
  Tablist,
  Tab,
  SegmentedControl,
  Badge,
  Select,
  Code
} from 'evergreen-ui';
import {
  GET_SYSTEM_PERFORMANCE,
  GET_GRAPHQL_PERFORMANCE,
  GET_FAILED_LOGINS
} from '../../../graphql/queries/Auth/QueryAuth';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * SystemHealthMonitor component
 * Displays system health metrics including server performance, GraphQL query performance, and failed login attempts
 */
const SystemHealthMonitor = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState(7); // Default to 7 days
  const tabs = ['Server Performance', 'GraphQL Performance', 'Failed Logins'];

  // Fetch system performance data
  const {
    loading: perfLoading,
    error: perfError,
    data: perfData,
    refetch: refetchPerf
  } = useQuery(GET_SYSTEM_PERFORMANCE, {
    variables: { days: timeRange },
    fetchPolicy: 'network-only'
  });

  // Fetch GraphQL performance data
  const {
    loading: graphqlLoading,
    error: graphqlError,
    data: graphqlData,
    refetch: refetchGraphql
  } = useQuery(GET_GRAPHQL_PERFORMANCE, {
    variables: { days: timeRange },
    fetchPolicy: 'network-only'
  });

  // Fetch failed login attempts data
  const {
    loading: loginLoading,
    error: loginError,
    data: loginData,
    refetch: refetchLogins
  } = useQuery(GET_FAILED_LOGINS, {
    variables: { days: timeRange },
    fetchPolicy: 'network-only'
  });

  // Refetch data when time range changes
  useEffect(() => {
    refetchPerf({ days: timeRange });
    refetchGraphql({ days: timeRange });
    refetchLogins({ days: timeRange });
  }, [timeRange, refetchPerf, refetchGraphql, refetchLogins]);

  // Parse the JSON data
  const parseJsonData = (jsonString) => {
    if (!jsonString) return null;

    try {
      // Handle double-encoded JSON
      const parsedOnce = JSON.parse(jsonString);
      if (typeof parsedOnce === 'string') {
        try {
          return JSON.parse(parsedOnce);
        } catch (e) {
          console.error('Error parsing inner JSON:', e);
          return parsedOnce;
        }
      }
      return parsedOnce;
    } catch (e) {
      console.error('Error parsing JSON:', e);
      return null;
    }
  };

  // Parse the data
  const systemPerformance = parseJsonData(perfData?.systemPerformance);
  const graphqlPerformance = parseJsonData(graphqlData?.graphqlPerformance);
  const failedLogins = parseJsonData(loginData?.failedLogins);

  // Debug data
  useEffect(() => {
    // Removed console logs
  }, [systemPerformance, graphqlPerformance, failedLogins]);

  // Handle time range change
  const handleTimeRangeChange = (event) => {
    setTimeRange(parseInt(event.target.value, 10));
  };

  // Prepare chart data for system performance
  const prepareSystemPerformanceChartData = () => {
    if (!systemPerformance || !systemPerformance.history) return null;

    const cpuData = systemPerformance.history.cpu_usage || [];
    const memoryData = systemPerformance.history.memory_usage || [];
    const diskData = systemPerformance.history.disk_usage || [];

    // Format dates for display
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    };

    return {
      labels: cpuData.map(item => formatDate(item.timestamp)),
      datasets: [
        {
          label: 'CPU Usage (%)',
          data: cpuData.map(item => item.value),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.1
        },
        {
          label: 'Memory Usage (%)',
          data: memoryData.map(item => item.value),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          tension: 0.1
        },
        {
          label: 'Disk Usage (%)',
          data: diskData.map(item => item.value),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for GraphQL performance
  const prepareGraphQLPerformanceChartData = () => {
    if (!graphqlPerformance || !graphqlPerformance.daily_averages) return null;

    const dailyData = graphqlPerformance.daily_averages || [];

    // Format dates for display
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    };

    return {
      labels: dailyData.map(item => formatDate(item.day)),
      datasets: [
        {
          label: 'Avg. Execution Time (ms)',
          data: dailyData.map(item => item.avg_time),
          borderColor: 'rgb(153, 102, 255)',
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          tension: 0.1,
          yAxisID: 'y'
        },
        {
          label: 'Query Count',
          data: dailyData.map(item => item.count),
          borderColor: 'rgb(255, 159, 64)',
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          tension: 0.1,
          yAxisID: 'y1'
        }
      ]
    };
  };

  // Prepare chart data for failed logins
  const prepareFailedLoginsChartData = () => {
    if (!failedLogins || !failedLogins.daily_attempts) return null;

    const dailyData = failedLogins.daily_attempts || [];

    // Format dates for display
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    };

    return {
      labels: dailyData.map(item => formatDate(item.day)),
      datasets: [
        {
          label: 'Failed Login Attempts',
          data: dailyData.map(item => item.count),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.1
        }
      ]
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'System Performance',
      },
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  // GraphQL chart options with dual Y axes
  const graphqlChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'GraphQL Performance',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Execution Time (ms)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: 'Query Count'
        }
      },
    }
  };

  // Render server performance tab
  const renderServerPerformanceTab = () => {
    if (perfLoading) {
      return (
        <Pane display="flex" alignItems="center" justifyContent="center" height={300}>
          <Spinner />
        </Pane>
      );
    }

    if (perfError) {
      return (
        <Alert intent="danger" title="Error loading system performance data">
          {perfError.message}
        </Alert>
      );
    }

    if (!systemPerformance) {
      return (
        <Alert intent="warning" title="No system performance data available">
          No data available for the selected time range.
        </Alert>
      );
    }

    const chartData = prepareSystemPerformanceChartData();

    return (
      <Pane>
        <Pane display="flex" flexWrap="wrap" marginBottom={24}>
          <Card
            elevation={0}
            backgroundColor="#f9f9fb"
            padding={16}
            marginRight={16}
            marginBottom={16}
            width={200}
            height={100}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Heading size={700} marginBottom={8}>{systemPerformance.current?.cpu_usage || 0}%</Heading>
            <Text size={300}>CPU Usage</Text>
          </Card>
          <Card
            elevation={0}
            backgroundColor="#f9f9fb"
            padding={16}
            marginRight={16}
            marginBottom={16}
            width={200}
            height={100}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Heading size={700} marginBottom={8}>{systemPerformance.current?.memory_usage || 0}%</Heading>
            <Text size={300}>Memory Usage</Text>
          </Card>
          <Card
            elevation={0}
            backgroundColor="#f9f9fb"
            padding={16}
            marginRight={16}
            marginBottom={16}
            width={200}
            height={100}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Heading size={700} marginBottom={8}>{systemPerformance.current?.disk_usage || 0}%</Heading>
            <Text size={300}>Disk Usage</Text>
          </Card>
        </Pane>

        <Pane height={300} marginBottom={24}>
          {chartData ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <Alert intent="warning">No historical data available</Alert>
          )}
        </Pane>
      </Pane>
    );
  };

  // Render GraphQL performance tab
  const renderGraphQLPerformanceTab = () => {
    if (graphqlLoading) {
      return (
        <Pane display="flex" alignItems="center" justifyContent="center" height={300}>
          <Spinner />
        </Pane>
      );
    }

    if (graphqlError) {
      return (
        <Alert intent="danger" title="Error loading GraphQL performance data">
          {graphqlError.message}
        </Alert>
      );
    }

    if (!graphqlPerformance) {
      return (
        <Alert intent="warning" title="No GraphQL performance data available">
          No data available for the selected time range.
        </Alert>
      );
    }

    const chartData = prepareGraphQLPerformanceChartData();

    return (
      <Pane>
        <Pane height={300} marginBottom={24}>
          {chartData ? (
            <Line data={chartData} options={graphqlChartOptions} />
          ) : (
            <Alert intent="warning">No historical data available</Alert>
          )}
        </Pane>

        <Heading size={500} marginBottom={8}>Slowest Queries</Heading>
        <Table marginBottom={24}>
          <Table.Head>
            <Table.TextHeaderCell>Operation</Table.TextHeaderCell>
            <Table.TextHeaderCell>Execution Time (ms)</Table.TextHeaderCell>
            <Table.TextHeaderCell>Timestamp</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {graphqlPerformance.slowest_queries?.map((query, index) => (
              <Table.Row key={index} height={24}>
                <Table.TextCell paddingY={6}>{query.operation_name}</Table.TextCell>
                <Table.TextCell paddingY={6} isNumber>{query.execution_time.toFixed(2)}</Table.TextCell>
                <Table.TextCell paddingY={6}>{new Date(query.timestamp).toLocaleString()}</Table.TextCell>
              </Table.Row>
            )) || (
              <Table.Row height={24}>
                <Table.TextCell paddingY={6}>No data available</Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>

        <Heading size={500} marginBottom={8}>Most Frequent Queries</Heading>
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Operation</Table.TextHeaderCell>
            <Table.TextHeaderCell>Count</Table.TextHeaderCell>
            <Table.TextHeaderCell>Avg. Time (ms)</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {graphqlPerformance.most_frequent_queries?.map((query, index) => (
              <Table.Row key={index} height={24}>
                <Table.TextCell paddingY={6}>{query.operation_name}</Table.TextCell>
                <Table.TextCell paddingY={6} isNumber>{query.count}</Table.TextCell>
                <Table.TextCell paddingY={6} isNumber>{query.avg_time.toFixed(2)}</Table.TextCell>
              </Table.Row>
            )) || (
              <Table.Row height={24}>
                <Table.TextCell paddingY={6}>No data available</Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Pane>
    );
  };

  // Render failed logins tab
  const renderFailedLoginsTab = () => {
    if (loginLoading) {
      return (
        <Pane display="flex" alignItems="center" justifyContent="center" height={300}>
          <Spinner />
        </Pane>
      );
    }

    if (loginError) {
      return (
        <Alert intent="danger" title="Error loading failed login data">
          {loginError.message}
        </Alert>
      );
    }

    if (!failedLogins) {
      return (
        <Alert intent="warning" title="No failed login data available">
          No data available for the selected time range.
        </Alert>
      );
    }

    const chartData = prepareFailedLoginsChartData();

    return (
      <Pane>
        <Pane display="flex" alignItems="center" marginBottom={16}>
          <Heading size={500} marginRight={16}>Total Failed Attempts:</Heading>
          <Badge color="red" size={300}>{failedLogins.total_attempts || 0}</Badge>
        </Pane>

        <Pane height={300} marginBottom={24}>
          {chartData ? (
            <Line data={chartData} options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                title: {
                  ...chartOptions.plugins.title,
                  text: 'Failed Login Attempts'
                }
              }
            }} />
          ) : (
            <Alert intent="warning">No historical data available</Alert>
          )}
        </Pane>

        <Pane display="flex" flexWrap="wrap">
          <Pane width="50%" paddingRight={8}>
            <Heading size={500} marginBottom={8}>Top Failed Emails</Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Email</Table.TextHeaderCell>
                <Table.TextHeaderCell>Count</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {failedLogins.attempts_by_email?.map((item, index) => (
                  <Table.Row key={index} height={24}>
                    <Table.TextCell paddingY={6}>{item.email}</Table.TextCell>
                    <Table.TextCell paddingY={6} isNumber>{item.count}</Table.TextCell>
                  </Table.Row>
                )) || (
                  <Table.Row height={24}>
                    <Table.TextCell paddingY={6}>No data available</Table.TextCell>
                    <Table.TextCell paddingY={6}></Table.TextCell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Pane>

          <Pane width="50%" paddingLeft={8}>
            <Heading size={500} marginBottom={8}>Top Failed IPs</Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>IP Address</Table.TextHeaderCell>
                <Table.TextHeaderCell>Count</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {failedLogins.attempts_by_ip?.map((item, index) => (
                  <Table.Row key={index} height={24}>
                    <Table.TextCell paddingY={6}>{item.ip_address}</Table.TextCell>
                    <Table.TextCell paddingY={6} isNumber>{item.count}</Table.TextCell>
                  </Table.Row>
                )) || (
                  <Table.Row height={24}>
                    <Table.TextCell paddingY={6}>No data available</Table.TextCell>
                    <Table.TextCell paddingY={6}></Table.TextCell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Pane>
        </Pane>

        <Heading size={500} marginTop={24} marginBottom={8}>Recent Failed Attempts</Heading>
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Email</Table.TextHeaderCell>
            <Table.TextHeaderCell>IP Address</Table.TextHeaderCell>
            <Table.TextHeaderCell>Timestamp</Table.TextHeaderCell>
            <Table.TextHeaderCell>Reason</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {failedLogins.recent_attempts?.slice(0, 10).map((attempt, index) => (
              <Table.Row key={index} height={24}>
                <Table.TextCell paddingY={6}>{attempt.email}</Table.TextCell>
                <Table.TextCell paddingY={6}>{attempt.ip_address}</Table.TextCell>
                <Table.TextCell paddingY={6}>{new Date(attempt.timestamp).toLocaleString()}</Table.TextCell>
                <Table.TextCell paddingY={6}>{attempt.reason}</Table.TextCell>
              </Table.Row>
            )) || (
              <Table.Row height={24}>
                <Table.TextCell paddingY={6}>No data available</Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
                <Table.TextCell paddingY={6}></Table.TextCell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Pane>
    );
  };

  // Debug component to display authentication information
  const renderDebugInfo = () => {
    return (
      <Pane background="tint2" padding={16} marginBottom={16} borderRadius={4}>
        <Heading size={500} marginBottom={8}>Debug Information</Heading>
        <Text>Token: {localStorage.getItem('token') ? 'Present' : 'Missing'}</Text>
        <Code marginTop={8} display="block">
          {`Token: ${localStorage.getItem('token') ? localStorage.getItem('token').substring(0, 10) + '...' : 'None'}`}
        </Code>
        <Text marginTop={8}>User ID: {localStorage.getItem('userid') || 'None'}</Text>
        <Text marginTop={8}>Publisher: {localStorage.getItem('publisher') || 'None'}</Text>

        {perfError && (
          <Pane marginTop={16}>
            <Heading size={400} marginBottom={4}>System Performance Error:</Heading>
            <Code display="block">{perfError.message}</Code>
            {perfError.graphQLErrors && perfError.graphQLErrors.map((error, index) => (
              <Code key={index} display="block" marginTop={4}>
                {JSON.stringify(error)}
              </Code>
            ))}
          </Pane>
        )}
      </Pane>
    );
  };

  return (
    <Pane elevation={1} background="white" padding={16} marginBottom={16}>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>System Health Monitoring</Heading>
        <Pane display="flex" alignItems="center">
          <Text marginRight={8}>Time Range:</Text>
          <Select value={timeRange} onChange={handleTimeRangeChange}>
            <option value="1">Last 24 Hours</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </Select>
        </Pane>
      </Pane>

      {/* Add debug information */}
      {renderDebugInfo()}

      <Tablist marginBottom={16}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab}
            id={tab}
            onSelect={() => setSelectedTab(index)}
            isSelected={index === selectedTab}
            aria-controls={`panel-${tab}`}
          >
            {tab}
          </Tab>
        ))}
      </Tablist>

      <Pane padding={16} background="tint1" borderRadius={3}>
        {selectedTab === 0 && renderServerPerformanceTab()}
        {selectedTab === 1 && renderGraphQLPerformanceTab()}
        {selectedTab === 2 && renderFailedLoginsTab()}
      </Pane>
    </Pane>
  );
};

export default SystemHealthMonitor;
